import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import {
  ArrowLeft, Clock, CheckCircle2, XCircle, AlertTriangle,
  User, Building2, Calendar, Camera, RefreshCw,
} from "lucide-react";

const MX_TZ = "America/Mexico_City";

function fmtDateTime(isoStr) {
  if (!isoStr) return "—";
  return new Date(isoStr).toLocaleString("es-MX", {
    timeZone: MX_TZ,
    dateStyle: "medium",
    timeStyle: "short",
  });
}

function fmtTime(isoStr) {
  if (!isoStr) return "—";
  return new Date(isoStr).toLocaleTimeString("es-MX", {
    timeZone: MX_TZ,
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

const STATUS_LABEL = {
  pendiente: "Pendiente",
  en_progreso: "En progreso",
  completado: "Completado",
  atrasado: "Atrasado",
  omitido: "Omitido",
};

const STATUS_COLOR = {
  pendiente: "bg-slate-100 text-slate-700",
  en_progreso: "bg-yellow-100 text-yellow-800",
  completado: "bg-green-100 text-green-800",
  atrasado: "bg-red-100 text-red-800",
  omitido: "bg-slate-100 text-slate-400",
};

const CAT_LABEL = {
  limpieza_banos: "Banos",
  limpieza_cocina: "Cocina",
  limpieza_comedor: "Comedor",
  limpieza_general: "General",
};

const TIMELINE_STEPS = [
  { key: "pendiente", label: "Creada" },
  { key: "claimed", label: "Tomada" },
  { key: "en_progreso", label: "Iniciada" },
  { key: "completado", label: "Completada" },
];

function StatusTimeline({ ex }) {
  const step = () => {
    if (ex.status === "omitido") return -1;
    if (ex.status === "completado") return 3;
    if (ex.status === "en_progreso") return 2;
    if (ex.assigned_to_user_id) return 1;
    return 0;
  };
  const s = step();

  if (ex.status === "omitido") {
    return (
      <div className="flex items-center gap-2 text-slate-400 text-sm py-4">
        <XCircle className="w-5 h-5" />
        <span>Tarea omitida — {ex.skip_reason}</span>
      </div>
    );
  }

  if (ex.status === "atrasado") {
    return (
      <div className="flex items-center gap-2 text-red-600 text-sm py-4">
        <AlertTriangle className="w-5 h-5" />
        <span>Tarea atrasada — vencio a las {fmtTime(ex.due_at)}</span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-0 mt-2">
      {TIMELINE_STEPS.map((step, i) => (
        <div key={step.key} className="flex items-center flex-1 last:flex-none">
          <div className="flex flex-col items-center">
            <div
              className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                i <= s ? "bg-[#FFDF00] text-slate-900" : "bg-slate-200 text-slate-400"
              }`}
            >
              {i <= s ? <CheckCircle2 className="w-4 h-4" /> : i + 1}
            </div>
            <span className={`text-xs mt-1 ${i <= s ? "text-slate-700 font-medium" : "text-slate-400"}`}>
              {step.label}
            </span>
          </div>
          {i < TIMELINE_STEPS.length - 1 && (
            <div className={`flex-1 h-0.5 mx-1 mb-4 ${i < s ? "bg-[#FFDF00]" : "bg-slate-200"}`} />
          )}
        </div>
      ))}
    </div>
  );
}

export default function OpsLimpiezaDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [ex, setEx] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [notes, setNotes] = useState("");
  const [skipReason, setSkipReason] = useState("");
  const [showSkipForm, setShowSkipForm] = useState(false);
  const fileRef = useRef(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/ops/cleaning/executions/${id}`);
      setEx(res.data);
    } catch {
      setEx(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const role = user?.role;
  const uid = user?.id;

  const canClaim = ex &&
    ex.status === "pendiente" &&
    !ex.assigned_to_user_id &&
    (role !== "operativo" || !user?.position_code || user.position_code === ex.responsible_position);

  const canStart = ex &&
    ["pendiente", "atrasado"].includes(ex.status) &&
    (role !== "operativo" || ex.assigned_to_user_id === uid);

  const canComplete = ex &&
    ["en_progreso", "pendiente", "atrasado"].includes(ex.status) &&
    (role !== "operativo" || !ex.assigned_to_user_id || ex.assigned_to_user_id === uid);

  const canSkip = ex &&
    !["completado", "omitido"].includes(ex.status) &&
    ["corporate_admin", "branch_manager"].includes(role);

  const handleAction = async (action) => {
    setActionLoading(true);
    try {
      await api.post(`/ops/cleaning/executions/${id}/${action}`);
      await load();
    } catch (err) {
      alert(err.response?.data?.detail || "Error al ejecutar la accion");
    } finally {
      setActionLoading(false);
    }
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setPhotoPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleComplete = async () => {
    setActionLoading(true);
    try {
      const formData = new FormData();
      if (notes) formData.append("notes", notes);
      if (fileRef.current?.files[0]) {
        formData.append("file", fileRef.current.files[0]);
      }
      await api.post(`/ops/cleaning/executions/${id}/complete`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await load();
      setPhotoPreview(null);
      setNotes("");
    } catch (err) {
      alert(err.response?.data?.detail || "Error al completar la tarea");
    } finally {
      setActionLoading(false);
    }
  };

  const handleSkip = async () => {
    if (!skipReason.trim()) return;
    setActionLoading(true);
    try {
      await api.post(`/ops/cleaning/executions/${id}/skip`, { reason: skipReason });
      setShowSkipForm(false);
      await load();
    } catch (err) {
      alert(err.response?.data?.detail || "Error al omitir la tarea");
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[60vh]">
        <RefreshCw className="w-8 h-8 animate-spin text-slate-300" />
      </div>
    );
  }

  if (!ex) {
    return (
      <div className="p-6 text-center text-slate-400">
        <p>Ejecucion no encontrada.</p>
        <button onClick={() => navigate("/ops/limpieza")} className="mt-2 text-blue-600 text-sm">
          Volver al calendario
        </button>
      </div>
    );
  }

  const token = localStorage.getItem("chizy_token");
  const photoSrc = ex.photo_url
    ? `${API_URL}/api/ops/photos/${ex.photo_url}?auth=${token}`
    : null;

  return (
    <div className="p-4 md:p-6 space-y-6 max-w-4xl">
      {/* Back */}
      <button
        data-testid="back-btn"
        onClick={() => navigate("/ops/limpieza")}
        className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"
      >
        <ArrowLeft className="w-4 h-4" />
        Volver al calendario
      </button>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Columna izquierda — Info */}
        <div className="space-y-4">
          {/* Card principal */}
          <div data-testid="execution-info-card" className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
            <div className="flex items-start justify-between gap-2">
              <h1 className="text-xl font-bold text-slate-900 leading-tight">{ex.title}</h1>
              <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold flex-shrink-0 ${STATUS_COLOR[ex.status]}`}>
                {STATUS_LABEL[ex.status]}
              </span>
            </div>

            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <p className="text-xs text-slate-400 mb-0.5">Categoria</p>
                <p className="font-medium text-slate-700">{CAT_LABEL[ex.category] || ex.category}</p>
              </div>
              <div>
                <p className="text-xs text-slate-400 mb-0.5">Puesto</p>
                <p className="font-medium text-slate-700">{ex.responsible_position}</p>
              </div>
              <div>
                <p className="text-xs text-slate-400 mb-0.5">Programada</p>
                <p className="font-medium text-slate-700">{fmtTime(ex.scheduled_at)}</p>
              </div>
              <div>
                <p className="text-xs text-slate-400 mb-0.5">Vence</p>
                <p className="font-medium text-slate-700">{fmtTime(ex.due_at)}</p>
              </div>
              <div>
                <p className="text-xs text-slate-400 mb-0.5">Duracion estimada</p>
                <p className="font-medium text-slate-700">{ex.estimated_minutes} min</p>
              </div>
              <div>
                <p className="text-xs text-slate-400 mb-0.5">Foto requerida</p>
                <p className="font-medium text-slate-700">{ex.photo_required ? "Si" : "No"}</p>
              </div>
            </div>

            {ex.assigned_to_user_id && (
              <div className="flex items-center gap-2 text-sm text-slate-600 border-t pt-3">
                <User className="w-4 h-4" />
                <span>Asignado · tomado {fmtTime(ex.claimed_at)}</span>
              </div>
            )}
          </div>

          {/* Foto completada */}
          {ex.status === "completado" && (
            <div className="bg-white border border-slate-200 rounded-xl p-4">
              <h3 className="text-sm font-semibold text-slate-700 mb-3">Resultado</h3>
              {photoSrc && (
                <img
                  data-testid="completed-photo"
                  src={photoSrc}
                  alt="Foto de la tarea completada"
                  className="w-full rounded-lg object-cover max-h-48 mb-2"
                />
              )}
              {ex.notes && (
                <p className="text-sm text-slate-600 mt-2 italic">"{ex.notes}"</p>
              )}
              <p className="text-xs text-slate-400 mt-2">
                Completada a las {fmtTime(ex.completed_at)}
              </p>
            </div>
          )}

          {/* Link a rotación origen */}
          {ex.rotation_id && (
            <div className="text-xs text-slate-400">
              Plantilla:{" "}
              <Link
                to={`/ops/admin/cleaning-rotations/${ex.rotation_id}`}
                className="text-blue-600 hover:underline"
                data-testid="rotation-link"
              >
                Ver rotacion origen
              </Link>
            </div>
          )}
        </div>

        {/* Columna derecha — Acciones + Timeline */}
        <div className="space-y-4">
          {/* Timeline visual */}
          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <h3 className="text-sm font-semibold text-slate-700 mb-4">Estado de la tarea</h3>
            <StatusTimeline ex={ex} />
          </div>

          {/* Acciones */}
          <div data-testid="actions-card" className="bg-white border border-slate-200 rounded-xl p-5 space-y-3">
            <h3 className="text-sm font-semibold text-slate-700">Acciones</h3>

            {canClaim && (
              <button
                data-testid="claim-action-btn"
                onClick={() => handleAction("claim")}
                disabled={actionLoading}
                className="w-full py-2.5 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50 text-sm"
              >
                {actionLoading ? "..." : "Tomar tarea"}
              </button>
            )}

            {canStart && (
              <button
                data-testid="start-action-btn"
                onClick={() => handleAction("start")}
                disabled={actionLoading}
                className="w-full py-2.5 bg-yellow-500 text-white rounded-lg font-semibold hover:bg-yellow-600 disabled:opacity-50 text-sm"
              >
                {actionLoading ? "..." : "Iniciar tarea"}
              </button>
            )}

            {canComplete && (
              <div className="space-y-3 border border-slate-200 rounded-lg p-3">
                <p className="text-sm font-medium text-slate-700">Completar tarea</p>

                <div>
                  <label className="block text-xs text-slate-500 mb-1">
                    {ex.photo_required ? "Foto (obligatoria)" : "Foto (opcional)"}
                  </label>
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={handlePhotoChange}
                    data-testid="photo-input"
                    className="hidden"
                    id="photo-file"
                  />
                  <label
                    htmlFor="photo-file"
                    data-testid="take-photo-btn"
                    className="flex items-center gap-2 px-3 py-2 border border-slate-200 rounded-lg text-sm cursor-pointer hover:bg-slate-50 w-full"
                  >
                    <Camera className="w-4 h-4 text-slate-400" />
                    {photoPreview ? "Foto seleccionada" : "Tomar / subir foto"}
                  </label>
                  {photoPreview && (
                    <img
                      data-testid="photo-preview"
                      src={photoPreview}
                      alt="Preview"
                      className="mt-2 w-full max-h-40 object-cover rounded-lg"
                    />
                  )}
                </div>

                <textarea
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  placeholder="Notas adicionales (opcional)"
                  rows={2}
                  data-testid="notes-input"
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]"
                />

                <button
                  data-testid="complete-action-btn"
                  onClick={handleComplete}
                  disabled={actionLoading || (ex.photo_required && !fileRef.current?.files[0])}
                  className="w-full py-2.5 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 disabled:opacity-50 text-sm"
                >
                  {actionLoading ? "Guardando..." : "Confirmar completado"}
                </button>
              </div>
            )}

            {canSkip && !showSkipForm && (
              <button
                data-testid="show-skip-btn"
                onClick={() => setShowSkipForm(true)}
                className="w-full py-2 border border-slate-300 text-slate-600 rounded-lg text-sm hover:bg-slate-50"
              >
                Omitir con motivo
              </button>
            )}

            {showSkipForm && (
              <div className="space-y-2 border border-red-200 rounded-lg p-3 bg-red-50">
                <p className="text-sm font-medium text-red-800">Omitir tarea</p>
                <textarea
                  value={skipReason}
                  onChange={e => setSkipReason(e.target.value)}
                  placeholder="Motivo de omision (obligatorio)"
                  rows={2}
                  data-testid="skip-reason-detail-input"
                  className="w-full border border-red-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-300"
                />
                <div className="flex gap-2">
                  <button
                    onClick={() => setShowSkipForm(false)}
                    data-testid="skip-cancel-detail-btn"
                    className="flex-1 py-1.5 border border-slate-300 rounded text-sm hover:bg-white"
                  >
                    Cancelar
                  </button>
                  <button
                    data-testid="skip-confirm-detail-btn"
                    onClick={handleSkip}
                    disabled={!skipReason.trim() || actionLoading}
                    className="flex-1 py-1.5 bg-red-600 text-white rounded text-sm font-semibold hover:bg-red-700 disabled:opacity-50"
                  >
                    {actionLoading ? "..." : "Confirmar"}
                  </button>
                </div>
              </div>
            )}

            {["completado", "omitido"].includes(ex.status) && (
              <p data-testid="final-status-msg" className="text-sm text-slate-400 text-center py-2">
                Esta tarea ya fue {ex.status === "completado" ? "completada" : "omitida"}.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
